import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logoutUser } from '../store/actions/authActions';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler } from 'chart.js';
import { DocumentIcon, EyeIcon, CompleteIcon, ProgressIcon, CircularProgressIcon, ThumbsUpIcon, PlayIcon, RecentScriptIcon, MobileMenuIcon, MobileSearchIcon } from '../assets/svgs/svgs';
import { Lightbulb } from 'lucide-react';
import ChannelComparison from '../components/ChannelComparison';
import DateRangePicker from '../components/DateRangePicker';
import Sidebar from '../components/Sidebar';
import { fetchDashboardStats } from '../store/actions/dashboardActions';
import { googleConnectStatus, googleConnect } from '../store/actions/authActions';
import GoogleOAuthModal from '../components/GoogleOAuthModal';
import LogoutModal from '../components/common/LogoutModal';
import LoadingModal from '../components/common/LoadingModal';
import { formatNumber } from '../utils/numberFormatter';
import { getUserInitials, getUserDisplayName } from '../utils/userUtils';
import ProfilePicture from '../components/common/ProfilePicture';


ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const getCurrentMonthDates = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  
  const firstDayOfMonth = new Date(year, month, 1);
  const dayOfWeek = firstDayOfMonth.getDay();
  
  const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  
  const getMonday = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  };
  
  const monday = getMonday(firstDayOfMonth);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  
  return {
    startDate: formatDate(monday),
    endDate: formatDate(sunday)
  };
};

const Dashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const dashboardData = useSelector((state) => state.dashboard);
  const googleConnectionStatus = useSelector((state) => state.auth.googleConnectionStatus);
  const { startDate: defaultStartDate, endDate: defaultEndDate } = getCurrentMonthDates();
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [startDate, setStartDate] = useState(() => {
    if (dashboardData?.lastSelectedStartDate && dashboardData?.lastSelectedEndDate) {
      return dashboardData.lastSelectedStartDate;
    }
    return defaultStartDate;
  });
  const [endDate, setEndDate] = useState(() => {
    if (dashboardData?.lastSelectedStartDate && dashboardData?.lastSelectedEndDate) {
      return dashboardData.lastSelectedEndDate;
    }
    return defaultEndDate;
  });
  const [recentScriptsSearch, setRecentScriptsSearch] = useState('');
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showOAuthModal, setShowOAuthModal] = useState(false);
  const competitorChannels = useSelector((state) => state.competitorChannels);
  const isDashboardLoading = dashboardData?.loading;
  const isGoogleConnected = googleConnectionStatus?.isConnected === true;

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    dispatch(logoutUser());
    navigate('/signin');
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const monthlyViews = dashboardData?.monthlyViews || [];
  
  const weekdayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const weekdayMap = {
    'Monday': 'Mon',
    'Tuesday': 'Tue',
    'Wednesday': 'Wed',
    'Thursday': 'Thur',
    'Friday': 'Fri',
    'Saturday': 'Sat',
    'Sunday': 'Sun'
  };

  const getWeekdayFromDate = (dateStr) => {
    const date = new Date(dateStr);
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[date.getDay()];
  };

  const viewsByWeekday = {};
  
  monthlyViews.forEach(item => {
    const weekday = item.weekday || getWeekdayFromDate(item.date);
    if (weekday) {
      viewsByWeekday[weekday] = item.views || 0;
    }
  });

  const chartLabels = weekdayOrder.map(weekday => weekdayMap[weekday] || weekday.substring(0, 3));

  const chartViewsData = weekdayOrder.map(weekday => viewsByWeekday[weekday] || 0);

  const maxViews = chartViewsData.length > 0 
    ? Math.max(...chartViewsData) 
    : 0;
  
  const calculateYAxisMax = (maxValue) => {
    if (maxValue === 0) return 10;
    if (maxValue <= 10) {
      return Math.ceil(maxValue * 1.2);
    }
    const magnitude = Math.pow(10, Math.floor(Math.log10(maxValue)));
    const normalized = maxValue / magnitude;
    const rounded = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
    return rounded * magnitude;
  };
  
  const yAxisMax = calculateYAxisMax(maxViews);

  const chartData = {
    labels: chartLabels,
    datasets: [
      {
        label: 'Views Generated',
        data: chartViewsData,
        borderColor: '#78350F',
        backgroundColor: 'rgba(120, 53, 15, 0.1)',
        fill: true,
        tension: 0,
        pointRadius: 0,
        pointHoverRadius: 8,
        pointHoverBackgroundColor: '#1C1917',
        pointHoverBorderColor: '#ffffff',
        pointHoverBorderWidth: 2,
        borderWidth: 2.5,
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        titleColor: '#1C1917',
        bodyColor: '#1C1917',
        padding: 12,
        displayColors: false,
        callbacks: {
          title: function (context) {
            if (context[0]?.dataIndex !== undefined && chartLabels.length > 0) {
              const dataIndex = context[0].dataIndex;
              const weekdayFull = weekdayOrder[dataIndex];
              if (weekdayFull && monthlyViews.length > 0) {
                const matchingDates = monthlyViews.filter(item => item.weekday === weekdayFull);
                if (matchingDates.length > 0) {
                  const firstDate = new Date(matchingDates[0].date);
                  return firstDate.toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric', 
                    year: 'numeric'
                  });
                }
              }
            }
            return '';
          },
          label: function (context) {
            return 'Views: ' + context.parsed.y.toLocaleString();
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#78716C',
          font: {
            size: 12
          }
        },
        border: {
          display: false
        }
      },
      y: {
        beginAtZero: true,
        min: 0,
        max: yAxisMax,
        border: {
          display: false
        },
        grid: {
          display: false,
        },
        ticks: {
          color: '#78716C',
          font: {
            size: 12
          },
          stepSize: (() => {
            if (yAxisMax <= 10) return 1;
            const magnitude = Math.pow(10, Math.floor(Math.log10(yAxisMax)));
            return magnitude;
          })(),
          callback: function (value) {
            return formatNumber(value);
          }
        }
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  };

  const recentScripts = dashboardData?.recentScripts || [];
  const topVideos = dashboardData?.topVideos || [];

  const handleVideoClick = (videoUrl) => {
    if (videoUrl) window.open(videoUrl, '_blank');
  };

  const handleImageError = (e) => {
    e.target.style.display = 'none';
    const fallback = e.target.nextElementSibling;
    if (fallback) fallback.style.display = 'flex';
  };

  const handleContinueWithGoogle = async () => {
    try {
      const response = await dispatch(googleConnect());
      if (response?.success && response?.data?.authUrl) {
        window.location.href = response.data.authUrl;
      }
    } catch (error) {
      console.error('Error connecting Google:', error);
    }
  };

  const filteredRecentScripts = recentScripts.filter(script => {
    if (!recentScriptsSearch) return true;

    const searchTerm = recentScriptsSearch.toLowerCase();
    const title = script?.topic?.title?.toLowerCase() || script.title?.toLowerCase() || '';
    const status = script.status?.toLowerCase() || '';
    const date = script.date?.toLowerCase() || script.createdAt?.toLowerCase() || '';

    return title.includes(searchTerm) || status.includes(searchTerm) || date.includes(searchTerm);
  });

  const normalizePercentage = (value) => {
    if (value == null) return 0;

    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue) || typeof numValue !== 'number') return 0;

    const percentage = numValue > 1 ? numValue : numValue * 100;
    return Math.min(Math.max(percentage, 0), 100);
  };

  const isCompletedStatus = (status) => {
    return status === 'Completed' || status === 'completed' || status === 'COMPLETED';
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  useEffect(() => {
    if (googleConnectionStatus !== null) {
      setShowOAuthModal(!googleConnectionStatus?.isConnected);
      return;
    }
    
    const checkOAuthStatus = async () => {
      try {
        await dispatch(googleConnectStatus());
      } catch (error) {
        console.error('Error checking OAuth status:', error);
      }
    };

    checkOAuthStatus();
  }, [dispatch, googleConnectionStatus]);

  useEffect(() => {
    if (isGoogleConnected && startDate && endDate) {
      dispatch(fetchDashboardStats(startDate, endDate));
    }
  }, [isGoogleConnected, startDate, endDate, dispatch]);

  return (
    <div className="h-screen bg-main flex overflow-hidden">
      {sidebarOpen && (
        <div
          className="fixed inset-0 overlay-dark z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <Sidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        activePage="dashboard"
        onLogout={handleLogout}
      />

      <div className="flex-1 flex flex-col h-full overflow-hidden">
        <header className="bg-primary text-white py-4 px-4 lg:px-6 flex-shrink-0">
          <div className="flex items-center justify-between gap-4">
            <button
              onClick={toggleSidebar}
              className="lg:hidden p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <MobileMenuIcon />
            </button>

            <div className="flex items-center gap-3 ml-auto">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm border-2 border-white/30">
                {getUserInitials(user)}
              </div>
              <span className="text-sm font-medium hidden sm:inline">{getUserDisplayName(user)}</span>
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-6 overflow-y-auto">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-primary">Creator Dashboard</h1>
            <p className="text-secondary text-sm mt-1">Welcome Back, {user?.fullName || 'Mitchell'} Here's Your Performance Overview.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6 mb-6">
            <div className="card rounded-2xl px-6 py-4 lg:px-8 lg:py-6 relative min-h-[140px]">
              <div className="flex flex-col h-full">
                <p className="text-xs lg:text-sm text-[#78716C] mb-1 font-medium">Total Scripts Created</p>
                <h3 className="text-2xl lg:text-3xl font-bold text-[#1C1917] mt-1">{dashboardData?.totalScripts?.value?.toLocaleString() || '0'}</h3>
                <div className="flex items-center gap-1 mt-auto">
                  <span className="text-xs lg:text-sm text-[#78350F] font-bold">↑ {dashboardData?.totalScripts?.percentageChange || '0%'}</span>
                </div>
              </div>
              <div className="absolute bottom-4 right-4 lg:bottom-5 lg:right-5 w-12 h-12 lg:w-14 lg:h-14 bg-[#F1718D] rounded-md flex items-center justify-center">
                <DocumentIcon className="w-7 h-7 lg:w-8 lg:h-8 text-white" />
              </div>
            </div>

            <div className="card rounded-2xl px-6 py-4 lg:px-8 lg:py-6 relative min-h-[140px]">
              <div className="flex flex-col h-full">
                <p className="text-xs lg:text-sm text-[#78716C] mb-1 font-medium">Total Views Generated</p>
                <h3 className="text-2xl lg:text-3xl font-bold text-[#1C1917] mt-1">{dashboardData?.totalViews?.value?.toLocaleString() || '0'}</h3>
                <div className="flex items-center gap-1 mt-auto">
                  <span className="text-xs lg:text-sm text-[#78350F] font-bold">↑ {dashboardData?.totalViews?.percentageChange || '0%'}</span>
                </div>
              </div>
              <div className="absolute bottom-4 right-4 lg:bottom-5 lg:right-5 w-12 h-12 lg:w-14 lg:h-14 bg-[#60A5FA] rounded-md flex items-center justify-center">
                <EyeIcon className="w-6 h-6 lg:w-8 lg:h-8 text-white" />
              </div>
            </div>
          </div>

          {/* Views Generated Chart */}
          <div className="card rounded-2xl p-4 lg:p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-2 gap-4">
              <h3 className="text-base lg:text-lg font-semibold text-gray-900">View's Generated</h3>

              <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                onDateChange={(start, end) => {
                  setStartDate(start);
                  setEndDate(end);
                }}
              />
            </div>

            <p className="text-xs lg:text-sm text-gray-500 mb-6">Here Is Your Weekly Performance Stats</p>

            <div className="h-48 lg:h-64">
              <Line data={chartData} options={chartOptions} />
            </div>
          </div>

          {/* Top 5 Videos + Channel Comparison Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 mb-6">
            {/* Top 5 Performing Videos */}
            <div className="card rounded-2xl p-4 lg:p-6">
              <h3 className="text-base lg:text-lg font-semibold text-gray-900 mb-4">Top 5 Performing Videos</h3>
              {topVideos.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No videos available
                </div>
              ) : (
                <div className="space-y-3">
                  {topVideos.map((video, index) => (
                    <div key={video.video || index} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg transition-colors group">
                      <div className="relative w-12 h-12 rounded-lg flex-shrink-0">
                        {video.thumbnail ? (
                          <>
                            <img
                              src={video.thumbnail}
                              alt={`Video ${video.video}`}
                              className="w-12 h-12 rounded-lg object-cover"
                              onError={handleImageError}
                            />
                            <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-gray-200 absolute inset-0 hidden">
                              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </div>
                          </>
                        ) : (
                          <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-gray-200">
                            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <h4 className="font-medium text-sm text-gray-900 truncate">
                          {video.title || `Video ${video.videoId || video.video || index + 1}`}
                        </h4>
                        <div className="flex items-center gap-3 text-xs mt-0.5">
                          <span className="flex items-center gap-1 text-[#7F7F7F]">
                            <EyeIcon className="w-3 h-3" />
                            {video.views?.toLocaleString() || '0'}
                          </span>
                          <span className="flex items-center gap-1 text-[#7F7F7F]">
                            <ThumbsUpIcon />
                            {video.likeCount?.toLocaleString() || video.likes?.toLocaleString() || '0'}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleVideoClick(video.url)}
                        className="w-8 h-8 rounded-full flex items-center justify-center bg-[#653600] hover:bg-[#4a2800] transition-colors cursor-pointer flex-shrink-0"
                        title="Watch Video"
                      >
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Channel Comparison */}
            <div className="card rounded-2xl p-4 lg:p-6">
              <div className="min-h-[200px]">
                <ChannelComparison
                  comparisonStats={competitorChannels?.channelComparison}
                />
              </div>
            </div>
          </div>

          {/*Recent Scripts */}
          <div className="mb-6">
            <div className="card rounded-2xl p-4 lg:p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                <h3 className="text-base lg:text-lg font-semibold text-gray-900">Recent Scripts</h3>
                {recentScripts.length > 0 && (
                  <div className="relative w-full sm:w-auto sm:min-w-[200px] lg:min-w-[280px]">
                    <input
                      type="text"
                      placeholder="Search..."
                      value={recentScriptsSearch}
                      onChange={(e) => setRecentScriptsSearch(e.target.value)}
                      className="w-full px-4 py-2 pr-10 rounded-lg border border-gray-300 bg-white text-gray-700 placeholder:text-gray-400 focus:outline-none focus:border-gray-400 text-sm transition-all"
                    />
                    <MobileSearchIcon className="w-5 h-5 absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                  </div>
                )}
              </div>

              <div className="space-y-3">
                {recentScripts.length === 0 ? (
                  <div className="text-center py-6 text-gray-500">
                    No recent scripts available.
                  </div>
                ) : filteredRecentScripts.length > 0 ? (
                  filteredRecentScripts.slice(0, 3).map((script) => {
                    const scriptId = script?.id || script?._id || `script-${Math.random()}`;
                    return (
                    <div key={scriptId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors gap-3">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <ProfilePicture
                          profilePicture={script?.user?.profilePicture}
                          user={user}
                          size="w-10 h-10"
                          className="flex-shrink-0"
                        />
                        <div className="min-w-0 flex-1">
                          <h4 className="font-semibold text-sm text-gray-900 truncate">{script?.topic?.title || 'Untitled Script'}</h4>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {script.date && (
                              <span className="tag text-xs">{script.date}</span>
                            )}
                            {script.createdAt && !script.date && (
                              <span className="tag text-xs">{formatDate(script.createdAt)}</span>
                            )}
                            {script.status && (
                              <span className={`px-2 py-0.5 text-xs rounded-full flex items-center gap-1 ${isCompletedStatus(script.status) ? 'status-badge-completed' : 'status-badge-inprogress'
                                }`}>
                                {isCompletedStatus(script.status) ? <CompleteIcon /> : <ProgressIcon />}
                                {script.status}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          const scriptId = script?.id || script?._id;
                          if (scriptId) {
                            navigate('/script-details', {
                              state: {
                                script: script,
                                scriptId: scriptId
                              }
                            });
                          }
                        }}
                        className="btn-dark-brown-filled px-3 sm:px-4 py-2 text-xs sm:text-sm whitespace-nowrap flex-shrink-0"
                      >
                        View Script
                      </button>
                    </div>
                    );
                  })
                ) : (
                  <div className="text-center py-6 text-gray-500">
                    No scripts found matching your search.
                  </div>
                )}
              </div>

              {recentScripts.length > 0 && (
                <div className="flex justify-end mt-4">
                  <button
                    onClick={() => navigate('/my-scripts')}
                    className="text-primary font-medium hover:underline text-sm"
                  >
                    View All Scripts
                  </button>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
      <LogoutModal
        showLogoutModal={showLogoutModal}
        onConfirm={confirmLogout}
        onCancel={cancelLogout}
      />
      {showOAuthModal && (
        <GoogleOAuthModal
          onContinue={handleContinueWithGoogle}
          onClose={() => setShowOAuthModal(false)}
        />
      )}
      <LoadingModal
        isLoading={isGoogleConnected && isDashboardLoading}
        title="Loading Dashboard"
        message="Fetching your performance data..."
      />
    </div>
  );
};

export default Dashboard;